import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import CurrencyFormat from "react-currency-format";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const BranchWiseComparison = ({ salesData }) => {
  // Filter data for F11 and G13 branches
  // F-11 has branch_id = 15, G-13 has branch_id = 1
  const branchData = useMemo(() => {
    if (!salesData || salesData.length === 0) {
      return {
        f11: {
          data: [],
          totalPrice: 0,
          totalDiscount: 0,
          totalTax: 0,
          totalNetPrice: 0,
          newSales: 0,
          renewSales: 0,
          quantity: 0,
          packages: 0,
          topPackages: [],
          categories: [],
          branchName: "F-11",
        },
        g13: {
          data: [],
          totalPrice: 0,
          totalDiscount: 0,
          totalTax: 0,
          totalNetPrice: 0,
          newSales: 0,
          renewSales: 0,
          quantity: 0,
          packages: 0,
          topPackages: [],
          categories: [],
          branchName: "G-13",
        },
      };
    }

    const f11Data = salesData.filter(item => {
      // Check by branch_id first (F-11 = 15)
      if (item.branch_id === 15 || item.branch_id === '15') {
        return true;
      }
      // Check by branch_name variations
      if (item.branch_name) {
        const branchName = item.branch_name.toLowerCase();
        return branchName.includes('f11') || branchName.includes('f-11');
      }
      return false;
    });
    
    const g13Data = salesData.filter(item => {
      // Check by branch_id first (G-13 = 1)
      if (item.branch_id === 1 || item.branch_id === '1') {
        return true;
      }
      // Check by branch_name variations
      if (item.branch_name) {
        const branchName = item.branch_name.toLowerCase();
        return branchName.includes('g13') || branchName.includes('g-13');
      }
      return false;
    });

    // Calculate totals for F11
    const f11TotalPrice = f11Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
    const f11TotalDiscount = f11Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
    const f11TotalTax = f11Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
    const f11TotalNetPrice = f11Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
    const f11NewSales = f11Data.filter(item => item.sale_type === "New").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
    const f11RenewSales = f11Data.filter(item => item.sale_type === "Renew").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
    const f11Quantity = f11Data.length;
    const f11Packages = [...new Set(f11Data.map(item => item.package_name).filter(Boolean))].length;

    // Calculate totals for G13
    const g13TotalPrice = g13Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_price) || 0), 0);
    const g13TotalDiscount = g13Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_discount) || 0), 0);
    const g13TotalTax = g13Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_tax) || 0), 0);
    const g13TotalNetPrice = g13Data.reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
    const g13NewSales = g13Data.filter(item => item.sale_type === "New").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
    const g13RenewSales = g13Data.filter(item => item.sale_type === "Renew").reduce((sum, item) => sum + (parseFloat(item.order_detail_net_price) || 0), 0);
    const g13Quantity = g13Data.length;
    const g13Packages = [...new Set(g13Data.map(item => item.package_name).filter(Boolean))].length;

    // Package-wise breakdown for F11
    const f11PackageMap = {};
    f11Data.forEach(item => {
      const pkgName = item.package_name || "Unknown";
      if (!f11PackageMap[pkgName]) {
        f11PackageMap[pkgName] = {
          name: pkgName,
          quantity: 0,
          price: 0,
          discount: 0,
          tax: 0,
          netPrice: 0,
        };
      }
      f11PackageMap[pkgName].quantity += 1;
      f11PackageMap[pkgName].price += parseFloat(item.order_detail_price || 0);
      f11PackageMap[pkgName].discount += parseFloat(item.order_detail_discount || 0);
      f11PackageMap[pkgName].tax += parseFloat(item.order_detail_tax || 0);
      f11PackageMap[pkgName].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    const f11TopPackages = Object.values(f11PackageMap)
      .sort((a, b) => b.netPrice - a.netPrice)
      .slice(0, 10);

    // Package-wise breakdown for G13
    const g13PackageMap = {};
    g13Data.forEach(item => {
      const pkgName = item.package_name || "Unknown";
      if (!g13PackageMap[pkgName]) {
        g13PackageMap[pkgName] = {
          name: pkgName,
          quantity: 0,
          price: 0,
          discount: 0,
          tax: 0,
          netPrice: 0,
        };
      }
      g13PackageMap[pkgName].quantity += 1;
      g13PackageMap[pkgName].price += parseFloat(item.order_detail_price || 0);
      g13PackageMap[pkgName].discount += parseFloat(item.order_detail_discount || 0);
      g13PackageMap[pkgName].tax += parseFloat(item.order_detail_tax || 0);
      g13PackageMap[pkgName].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    const g13TopPackages = Object.values(g13PackageMap)
      .sort((a, b) => b.netPrice - a.netPrice)
      .slice(0, 10);

    // Category-wise breakdown for F11
    const f11CategoryMap = {};
    f11Data.forEach(item => {
      const catName = item.category_name || "Uncategorized";
      if (!f11CategoryMap[catName]) {
        f11CategoryMap[catName] = {
          name: catName,
          quantity: 0,
          netPrice: 0,
        };
      }
      f11CategoryMap[catName].quantity += 1;
      f11CategoryMap[catName].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    const f11Categories = Object.values(f11CategoryMap)
      .sort((a, b) => b.netPrice - a.netPrice);

    // Category-wise breakdown for G13
    const g13CategoryMap = {};
    g13Data.forEach(item => {
      const catName = item.category_name || "Uncategorized";
      if (!g13CategoryMap[catName]) {
        g13CategoryMap[catName] = {
          name: catName,
          quantity: 0,
          netPrice: 0,
        };
      }
      g13CategoryMap[catName].quantity += 1;
      g13CategoryMap[catName].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    const g13Categories = Object.values(g13CategoryMap)
      .sort((a, b) => b.netPrice - a.netPrice);

    return {
      f11: {
        data: f11Data,
        totalPrice: f11TotalPrice,
        totalDiscount: f11TotalDiscount,
        totalTax: f11TotalTax,
        totalNetPrice: f11TotalNetPrice,
        newSales: f11NewSales,
        renewSales: f11RenewSales,
        quantity: f11Quantity,
        packages: f11Packages,
        topPackages: f11TopPackages,
        categories: f11Categories,
        branchName: f11Data.length > 0 ? f11Data[0].branch_name : "F-11",
      },
      g13: {
        data: g13Data,
        totalPrice: g13TotalPrice,
        totalDiscount: g13TotalDiscount,
        totalTax: g13TotalTax,
        totalNetPrice: g13TotalNetPrice,
        newSales: g13NewSales,
        renewSales: g13RenewSales,
        quantity: g13Quantity,
        packages: g13Packages,
        topPackages: g13TopPackages,
        categories: g13Categories,
        branchName: g13Data.length > 0 ? g13Data[0].branch_name : "G-13",
      },
    };
  }, [salesData]);

  // Chart data for comparison
  const comparisonChartData = [
    {
      name: 'Total Sales',
      F11: branchData.f11.totalNetPrice,
      G13: branchData.g13.totalNetPrice,
    },
    {
      name: 'New Sales',
      F11: branchData.f11.newSales,
      G13: branchData.g13.newSales,
    },
    {
      name: 'Renew Sales',
      F11: branchData.f11.renewSales,
      G13: branchData.g13.renewSales,
    },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="fw-bold mb-1">{payload[0].name}</p>
          {payload.map((entry, index) => (
            <p key={index} className="mb-0" style={{ color: entry.color }}>
              {entry.dataKey}: <CurrencyFormat
                value={entry.value}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"Rs "}
              />
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Show message if no data available
  if (!salesData || salesData.length === 0) {
    return (
      <div className="alert alert-info">
        <h5><i className="bi bi-info-circle me-2"></i>No Sales Data Available</h5>
        <p className="mb-0">Please select a date range and click "Search" to view branch-wise comparison.</p>
      </div>
    );
  }

  // Show message if no data found for either branch
  if (branchData.f11.quantity === 0 && branchData.g13.quantity === 0) {
    // Get unique branch names from data for debugging
    const availableBranches = [...new Set(salesData.map(item => 
      `${item.branch_name || 'N/A'} (ID: ${item.branch_id || 'N/A'})`
    ).filter(Boolean))];
    
    return (
      <div className="alert alert-warning">
        <h5><i className="bi bi-exclamation-triangle me-2"></i>No Data Found for F-11 or G-13 Branches</h5>
        <p className="mb-2">The selected date range or filters may not include data from F-11 (branch_id: 15) or G-13 (branch_id: 1) branches.</p>
        <p className="mb-0"><strong>Available branches in current data:</strong> {availableBranches.length > 0 ? availableBranches.join(', ') : 'None'}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Comparison Cards */}
      <div className="row mb-4">
        <div className="col-12 col-md-6 mb-3">
          <div className="card border-primary">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-building me-2"></i>{branchData.f11.branchName}
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-6 mb-3">
                  <div className="text-muted small">Total Sales</div>
                  <h5 className="text-primary">
                    <CurrencyFormat
                      value={branchData.f11.totalNetPrice}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </h5>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Quantity</div>
                  <h5 className="text-primary">{branchData.f11.quantity}</h5>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">New Sales</div>
                  <div className="text-success">
                    <CurrencyFormat
                      value={branchData.f11.newSales}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Renew Sales</div>
                  <div className="text-info">
                    <CurrencyFormat
                      value={branchData.f11.renewSales}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Total Packages</div>
                  <div>{branchData.f11.packages}</div>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Profit (Net - Discount)</div>
                  <div className="text-success">
                    <CurrencyFormat
                      value={branchData.f11.totalNetPrice - branchData.f11.totalDiscount}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 mb-3">
          <div className="card border-success">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">
                <i className="bi bi-building me-2"></i>{branchData.g13.branchName}
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-6 mb-3">
                  <div className="text-muted small">Total Sales</div>
                  <h5 className="text-success">
                    <CurrencyFormat
                      value={branchData.g13.totalNetPrice}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </h5>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Quantity</div>
                  <h5 className="text-success">{branchData.g13.quantity}</h5>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">New Sales</div>
                  <div className="text-success">
                    <CurrencyFormat
                      value={branchData.g13.newSales}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Renew Sales</div>
                  <div className="text-info">
                    <CurrencyFormat
                      value={branchData.g13.renewSales}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Total Packages</div>
                  <div>{branchData.g13.packages}</div>
                </div>
                <div className="col-6 mb-3">
                  <div className="text-muted small">Profit (Net - Discount)</div>
                  <div className="text-success">
                    <CurrencyFormat
                      value={branchData.g13.totalNetPrice - branchData.g13.totalDiscount}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="card mb-4">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">
            <i className="bi bi-bar-chart me-2"></i>Sales Comparison Chart
          </h5>
        </div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={comparisonChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="F11" fill="#0088FE" name={branchData.f11.branchName} />
              <Bar dataKey="G13" fill="#00C49F" name={branchData.g13.branchName} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Comparison Table */}
      <div className="row mb-4">
        <div className="col-12 col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">{branchData.f11.branchName} - Financial Summary</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>Metric</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Total Price</td>
                      <td>
                        <CurrencyFormat
                          value={branchData.f11.totalPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Total Discount</td>
                      <td className="text-danger">
                        <CurrencyFormat
                          value={branchData.f11.totalDiscount}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Total Tax</td>
                      <td>
                        <CurrencyFormat
                          value={branchData.f11.totalTax}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr className="table-success">
                      <td><strong>Total Net Price</strong></td>
                      <td><strong>
                        <CurrencyFormat
                          value={branchData.f11.totalNetPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </strong></td>
                    </tr>
                    <tr>
                      <td>New Sales</td>
                      <td className="text-success">
                        <CurrencyFormat
                          value={branchData.f11.newSales}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Renew Sales</td>
                      <td className="text-info">
                        <CurrencyFormat
                          value={branchData.f11.renewSales}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0">{branchData.g13.branchName} - Financial Summary</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>Metric</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Total Price</td>
                      <td>
                        <CurrencyFormat
                          value={branchData.g13.totalPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Total Discount</td>
                      <td className="text-danger">
                        <CurrencyFormat
                          value={branchData.g13.totalDiscount}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Total Tax</td>
                      <td>
                        <CurrencyFormat
                          value={branchData.g13.totalTax}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr className="table-success">
                      <td><strong>Total Net Price</strong></td>
                      <td><strong>
                        <CurrencyFormat
                          value={branchData.g13.totalNetPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </strong></td>
                    </tr>
                    <tr>
                      <td>New Sales</td>
                      <td className="text-success">
                        <CurrencyFormat
                          value={branchData.g13.newSales}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Renew Sales</td>
                      <td className="text-info">
                        <CurrencyFormat
                          value={branchData.g13.renewSales}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Packages Comparison */}
      <div className="row mb-4">
        <div className="col-12 col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">{branchData.f11.branchName} - Top 10 Packages</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Package</th>
                      <th>Qty</th>
                      <th>Net Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchData.f11.topPackages.map((pkg, idx) => (
                      <tr key={idx}>
                        <td>{pkg.name.length > 30 ? pkg.name.substring(0, 30) + "..." : pkg.name}</td>
                        <td>{pkg.quantity}</td>
                        <td>
                          <CurrencyFormat
                            value={pkg.netPrice}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0">{branchData.g13.branchName} - Top 10 Packages</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Package</th>
                      <th>Qty</th>
                      <th>Net Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchData.g13.topPackages.map((pkg, idx) => (
                      <tr key={idx}>
                        <td>{pkg.name.length > 30 ? pkg.name.substring(0, 30) + "..." : pkg.name}</td>
                        <td>{pkg.quantity}</td>
                        <td>
                          <CurrencyFormat
                            value={pkg.netPrice}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Comparison */}
      <div className="row mb-4">
        <div className="col-12 col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">{branchData.f11.branchName} - Sales by Category</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Category</th>
                      <th>Qty</th>
                      <th>Net Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchData.f11.categories.map((cat, idx) => (
                      <tr key={idx}>
                        <td>{cat.name}</td>
                        <td>{cat.quantity}</td>
                        <td>
                          <CurrencyFormat
                            value={cat.netPrice}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0">{branchData.g13.branchName} - Sales by Category</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Category</th>
                      <th>Qty</th>
                      <th>Net Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchData.g13.categories.map((cat, idx) => (
                      <tr key={idx}>
                        <td>{cat.name}</td>
                        <td>{cat.quantity}</td>
                        <td>
                          <CurrencyFormat
                            value={cat.netPrice}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchWiseComparison;


